import { IconButton, toast } from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { reissueTrainingShareLink, revokeTrainingShareLink } from "@/api/trainingShareLinks";
import { PaginationFooter, ResourceBrowserLayout } from "@/components/browse";
import { FilterResultCount } from "@/components/filter";
import { HatTextModal } from "@/components/modal";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { createLogger } from "@/logging";
import { useRelativeTimeClock } from "@/utils/useRelativeTimeClock";
import { createShareLinkColumns } from "./shareLinkColumns";
import { shareLinkRevokeErrorMessage } from "./shareLinkMessages";
import type { ShareLinkScope } from "./shareLinkScope";
import type { ShareLinkTableRow } from "./ShareLinkTableRow";
import { ShareLinkTableSource } from "./ShareLinkTableSource";
import { useShareLinkIssuer } from "./useShareLinkIssuer";
import { tableStripError } from "@/components/table/tableStateMessages";

const logger = createLogger("page/training/shareLinksPanel");

export type ShareLinksPanelProps = {
  // 어느 목록인지. 페이지가 URL 에서 읽어 전달한다(조직 전체는 admin 만 가능하고, 서버도 403 을 반환한다).
  scope: ShareLinkScope;
};

// Training > 공유 링크 탭. 발급한 링크의 발행 기록이다. 링크 원문은 발급 응답에만 있어서 여기서는
// 다시 볼 수 없다. 대신 같은 파일로 새 링크를 만들거나(다시 발급) 링크를 무효로 만든다(폐기 = 기록 삭제).
//
// 범위(내 링크·전체)는 목록을 좁히는 축이 아니라 "어느 목록인가" 다. 그래서 필터 바가 아니라 페이지
// 제목 옆 스위처가 소유한다(rules.md §17 스코프 규약). 조회 축이 없으므로 필터 바를 두지 않는다.
export function ShareLinksPanel({ scope }: ShareLinksPanelProps): ReactNode {
  const [source] = useState(() => new ShareLinkTableSource({ initialScope: scope }));
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const isLoading = snapshot.loadState === "loading";
  const nowMs = useRelativeTimeClock();

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    void source.initialize();
    return () => source.dispose();
  }, [source]);

  // URL 범위가 바뀌면(스위처·뒤로가기) source 에 전달한다. 같은 값이면 no-op 이라 첫 마운트에는 다시 조회하지 않는다.
  useEffect(() => {
    void source.setScope(scope);
  }, [source, scope]);

  const issuer = useShareLinkIssuer();
  const { issue } = issuer;
  const handleReissue = useCallback(
    (row: ShareLinkTableRow) => {
      void issue(row.uuid, () => reissueTrainingShareLink(row.uuid)).then((issued) => {
        // 새 행이 맨 위에 추가된다. 성공했을 때만 첫 페이지를 다시 받는다. 첫 페이지가 캐시에 있어도
        // 캐시를 쓰지 않는다.
        if (issued === null) {
          return;
        }
        void source.refresh(1);
      });
    },
    [issue, source],
  );

  const [revokeTarget, setRevokeTarget] = useState<ShareLinkTableRow | null>(null);
  const [revoking, setRevoking] = useState(false);
  const handleConfirmRevoke = (): void => {
    if (revokeTarget === null || revoking) {
      return;
    }
    setRevoking(true);
    void revokeTrainingShareLink(revokeTarget.uuid)
      .then(() =>
        toast.success("공유 링크를 폐기했습니다. 이제 이 링크로는 파일을 받을 수 없습니다."),
      )
      .catch((error: unknown) => {
        logger.error("share link revoke failed", error);
        toast.error(shareLinkRevokeErrorMessage(error));
      })
      .finally(() => {
        // 성공이든 실패든 다시 읽는다. 404 는 다른 곳에서 이미 폐기되었다는 뜻이다.
        void source.refresh();
        if (aliveRef.current) {
          setRevoking(false);
          setRevokeTarget(null);
        }
      });
  };

  const columns = useMemo(
    () =>
      createShareLinkColumns({
        nowMs,
        showIssuer: scope === "all",
        reissuingId: issuer.pendingKey,
        onReissue: handleReissue,
        onRevoke: setRevokeTarget,
      }),
    [nowMs, scope, issuer.pendingKey, handleReissue],
  );

  return (
    <>
      <ResourceBrowserLayout
        ariaLabel="공유 링크 목록"
        summaryCount={snapshot.total}
        summaryLabel="공유 링크"
        summaryInFilter
        error={tableStripError(snapshot)}
        onRetry={() => void source.refresh()}
      >
        <div className={bulkActionBarStyles.host}>
          <BulkActionBar
            source={source}
            idleContent={<FilterResultCount count={snapshot.total} unitLabel="공유 링크" />}
            rightContent={
              <IconButton
                text={isLoading ? "로딩 중…" : "새로고침"}
                size="sm"
                variant="surface"
                disabled={isLoading}
                onClick={() => void source.refresh()}
              />
            }
            ariaLabel="공유 링크 목록 도구"
          />
          <HatDataTable
            source={source}
            columns={columns}
            emptyMessage={
              scope === "all"
                ? "아직 조직에서 발급한 공유 링크가 없습니다."
                : "아직 발급한 공유 링크가 없습니다. Evaluation의 Policy 목록이나 Session 창의 Checkpoint 탭에서 공유해 보세요."
            }
            loadingMessage="공유 링크를 불러오고 있습니다."
            ariaLabel="공유 링크 테이블"
            scrollable
            minWidth="960px"
            showIndex={false}
          />
        </div>
        <PaginationFooter
          page={snapshot.page}
          size={snapshot.size}
          total={snapshot.total}
          isLoading={isLoading}
          ariaLabel="공유 링크 목록 페이지 이동"
          mode="compact"
          hideWhenSinglePage
          onPrevious={() => void source.goToPage(Math.max(1, snapshot.page - 1))}
          onNext={() => void source.goToPage(snapshot.page + 1)}
        />
      </ResourceBrowserLayout>
      <HatTextModal
        open={revokeTarget !== null}
        label="공유 링크를 폐기합니다."
        eyebrow={
          revokeTarget === null
            ? undefined
            : `${revokeTarget.kindLabel} · ${revokeTarget.targetLabel}`
        }
        caption="받은 사람이 더 이상 이 링크로 파일을 받을 수 없고 발행 기록도 지워집니다. 이미 시작된 다운로드는 끝까지 진행될 수 있습니다."
        okText="폐기"
        error
        busy={revoking}
        onOkPress={handleConfirmRevoke}
        onCancelPress={() => setRevokeTarget(null)}
      />
      {issuer.revealModal}
    </>
  );
}
